/**
 * Knowledge Store - Zustand
 * Hanterar lästa artiklar och kunskapsprogression
 * 
 * OBS: Lästa artiklar lagras ENDAST i databasen (user.readArticles).
 * Denna store är endast en hjälpare för att komma åt data.
 */

import { create } from 'zustand';
import { apiPost, apiDelete } from '@/services/api-client';
import { logKnowledgeAction } from '@/lib/activity-logger';

export interface ReadArticle {
  articleId: string;
  areaKey: string;
  readAt: string;
  pointsAwarded: number;
}

interface KnowledgeState {
  // Actions
  markArticleAsRead: (articleId: string, areaKey: string, points: number) => Promise<void>;
  unmarkArticleAsRead: (articleId: string) => Promise<void>;
  isArticleRead: (articleId: string, readArticles?: ReadArticle[]) => boolean;
  getReadArticlesForArea: (areaKey: string, readArticles?: ReadArticle[]) => ReadArticle[];
  getTotalReadCount: (readArticles?: ReadArticle[]) => number;
  getTotalKnowledgePoints: (readArticles?: ReadArticle[]) => number;
}

export const useKnowledgeStore = create<KnowledgeState>()((_set, _get) => ({
  markArticleAsRead: async (articleId, areaKey, points) => {
    // Synka med backend - backend kommer uppdatera user.readArticles
    try {
      await apiPost<{ readArticles: ReadArticle[] }>('/users/me/articles/read', {
        articleId,
        areaKey,
        pointsAwarded: points,
      });
      logKnowledgeAction('READ_ARTICLE', articleId, { areaKey, points });
      
      // Update will happen automatically through backend response
      // Auth store will be updated when component re-fetches user data
    } catch (error) {
      console.error('Failed to sync read article to backend:', error);
      throw error;
    }
  },
  
  unmarkArticleAsRead: async (articleId) => {
    // Synka med backend - backend kommer uppdatera user.readArticles
    try {
      await apiDelete(`/users/me/articles/read/${articleId}`);
      
      // Update will happen automatically through backend response
      // Auth store will be updated when component re-fetches user data
    } catch (error) {
      console.error('Failed to unmark article as read:', error);
      throw error;
    }
  },
  
  isArticleRead: (articleId, readArticles = []) => {
    return readArticles.some(a => a.articleId === articleId);
  },
  
  getReadArticlesForArea: (areaKey, readArticles = []) => {
    return readArticles.filter(a => a.areaKey === areaKey);
  },
  
  getTotalReadCount: (readArticles = []) => {
    return readArticles.length;
  },
  
  getTotalKnowledgePoints: (readArticles = []) => {
    return readArticles.reduce((sum, a) => sum + a.pointsAwarded, 0);
  },
}));
